import { calcNetProfit, type Product, type Sale } from "@/context/AppContext";
import {
  STRATEGY_META,
  type BundleStrategyId,
  type StrategyMeta,
} from "@/features/bundleEngine";

export interface BundlePerformanceStat {
  strategyId: BundleStrategyId;
  meta: StrategyMeta;
  productCount: number;
  soldCount: number;
  avgRoi: number | null;
  /** Sample standard deviation of ROI (percentage points) across sold products. null if <2 samples. */
  roiStdDev: number | null;
  avgDaysToSell: number | null;
  totalNet: number;
}

export function computeBundlePerformance(
  products: Product[],
  sales: Sale[],
): BundlePerformanceStat[] {
  const productSaleMap = new Map<string, Sale>();
  for (const sale of sales) {
    if (sale.itemType === "Product") {
      const existing = productSaleMap.get(sale.itemId);
      if (
        !existing ||
        new Date(sale.saleDate).getTime() <
          new Date(existing.saleDate).getTime()
      ) {
        productSaleMap.set(sale.itemId, sale);
      }
    }
  }

  return STRATEGY_META.map((meta): BundlePerformanceStat => {
    const matching = products.filter((p) => p.bundleStrategyId === meta.id);
    let soldCount = 0;
    const roiSamples: number[] = [];
    let daysSum = 0;
    let daysCount = 0;
    let totalNet = 0;

    for (const product of matching) {
      const sale = productSaleMap.get(product.id);
      if (!sale) continue;
      soldCount += 1;
      const costBasis = product.costBasis ?? 0;
      const packagingCost = product.packagingCost ?? 0;
      const realizedProfit = calcNetProfit(sale) - costBasis;
      totalNet += realizedProfit;
      const invested = costBasis + packagingCost;
      if (invested > 0) {
        roiSamples.push((realizedProfit / invested) * 100);
      }
      const created = new Date(product.createdAt).getTime();
      const sold = new Date(sale.saleDate).getTime();
      if (
        Number.isFinite(created) &&
        Number.isFinite(sold) &&
        sold >= created
      ) {
        daysSum += (sold - created) / 86400000;
        daysCount += 1;
      }
    }

    const avgRoi =
      roiSamples.length > 0
        ? roiSamples.reduce((a, b) => a + b, 0) / roiSamples.length
        : null;
    let roiStdDev: number | null = null;
    if (roiSamples.length >= 2 && avgRoi != null) {
      const variance =
        roiSamples.reduce((s, v) => s + (v - avgRoi) ** 2, 0) /
        (roiSamples.length - 1);
      roiStdDev = Math.sqrt(variance);
    }

    return {
      strategyId: meta.id,
      meta,
      productCount: matching.length,
      soldCount,
      avgRoi,
      roiStdDev,
      avgDaysToSell: daysCount > 0 ? daysSum / daysCount : null,
      totalNet,
    };
  });
}
